/**
 * Customers MV2 query API — isolated from MV1 pages/customers/*.
 */
import api from "@/lib/api";
import { normalizePhone } from "@/features/customers/lib/phoneUtils";

export const CUSTOMER_STATS_QUERY_KEY = "customer-stats";
export const CUSTOMERS_LIST_QUERY_KEY = "customers-list";
export const CUSTOMER_TOPINS_QUERY_KEY = "customer-topins";
export const CUSTOMER_TOPINS_STATS_QUERY_KEY = "customer-topins-stats";
export const CUSTOMER_TOPINS_REFERRALS_QUERY_KEY = "customer-topins-referrals";
export const CUSTOMER_TOPINS_WALLETS_QUERY_KEY = "customer-topins-wallets";

export const LEADS_LIST_QUERY_KEY = "leads-list";
export const LEADS_STATS_QUERY_KEY = "leads-stats";
export const LEADS_FLAGS_QUERY_KEY = "leads-flags";
export const LEADS_EVENTS_QUERY_KEY = "leads-events";
export const LEADS_QUICK_NOTES_QUERY_KEY = "leads-quick-notes";
export const LEADS_SOCIETIES_QUERY_KEY = "leads-societies";

const TOPINS_LIST_LIMIT = 200;
const LEADS_LIST_LIMIT = 500;

export function buildCustomersListParams({ pageSize, page, filterType, search }) {
  const searchDigits = String(search ?? "").replace(/\D/g, "");
  const searchNorm = normalizePhone(search);
  const effectiveSearch = searchDigits ? (searchNorm.length === 10 ? searchNorm : "__invalid__") : undefined;

  return {
    limit: pageSize,
    offset: (page - 1) * pageSize,
    filter_type: filterType === "repeat" ? "repeat" : filterType === "new" ? "new" : undefined,
    search: effectiveSearch,
  };
}

export function buildLeadsActivityParams({ activityWindow, activityDate }) {
  const params = {};
  if (activityDate) {
    params.activityDate = activityDate;
  } else if (activityWindow && activityWindow !== "all") {
    params.activityWindow = activityWindow;
  }
  return params;
}

export function buildLeadsStatsParams({ eventKey, activityWindow, activityDate }) {
  return {
    ...buildLeadsActivityParams({ activityWindow, activityDate }),
    ...(eventKey ? { eventKey } : {}),
  };
}

export function buildLeadsListParams({
  search,
  eventKey,
  flagFilter,
  activityWindow,
  activityDate,
  statBucket,
  occasionWindow,
  limit = LEADS_LIST_LIMIT,
  offset = 0,
}) {
  const params = {
    limit,
    offset,
    sync: false,
    ...buildLeadsActivityParams({ activityWindow, activityDate }),
  };
  if (search?.trim()) params.search = search.trim();
  if (eventKey) params.eventKey = eventKey;
  if (flagFilter) params.flagId = flagFilter;
  if (statBucket) params.statBucket = statBucket;
  if (occasionWindow) params.occasionWindow = occasionWindow;
  return params;
}

export async function fetchCustomerStatsFromApi(signal) {
  const res = await api.get("/customers/stats", signal ? { signal } : undefined);
  return res.data;
}

export async function fetchCustomersListFromApi(params, signal) {
  if (params.search === "__invalid__") {
    return { customers: [], total: 0 };
  }
  const res = await api.get("/customers", {
    params,
    ...(signal ? { signal } : {}),
  });
  return {
    customers: res.data.customers || [],
    total: res.data.total || 0,
  };
}

export async function fetchCustomerTopinsStatsFromApi(signal) {
  const res = await api.get("/customer-topins/stats", signal ? { signal } : undefined);
  return res.data;
}

export async function fetchCustomerReferralsFromApi(signal) {
  const res = await api.get("/customer-topins/referrals", {
    params: { limit: TOPINS_LIST_LIMIT },
    ...(signal ? { signal } : {}),
  });
  return res.data.referrals || [];
}

export async function fetchCustomerWalletsFromApi(signal) {
  const res = await api.get("/customer-topins/wallets", {
    params: { limit: TOPINS_LIST_LIMIT },
    ...(signal ? { signal } : {}),
  });
  return res.data.wallets || [];
}

/** Composed TOPINS fetch — safe section fetchers; kept for guard wiring. */
export async function fetchCustomerTopinsFromApi(signal) {
  const [statsResult, referralsResult, walletsResult] = await Promise.allSettled([
    fetchCustomerTopinsStatsFromApi(signal),
    fetchCustomerReferralsFromApi(signal),
    fetchCustomerWalletsFromApi(signal),
  ]);
  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");

  const stats = statsResult.status === "fulfilled" ? statsResult.value : null;
  const referrals = referralsResult.status === "fulfilled" ? referralsResult.value : [];
  const wallets = walletsResult.status === "fulfilled" ? walletsResult.value : [];

  if (statsResult.status === "rejected") {
    console.error("Customers MV2: failed to fetch topins stats:", statsResult.reason);
  }
  if (referralsResult.status === "rejected") {
    console.error("Customers MV2: failed to fetch topins referrals:", referralsResult.reason);
  }
  if (walletsResult.status === "rejected") {
    console.error("Customers MV2: failed to fetch topins wallets:", walletsResult.reason);
  }

  return { stats, referrals, wallets };
}

export async function fetchLeadsFlagsFromApi(signal) {
  const res = await api.get("/leads/flags", signal ? { signal } : undefined);
  return res.data.flags || [];
}

export async function fetchLeadsEventsFromApi(signal) {
  const res = await api.get("/leads/events", signal ? { signal } : undefined);
  return res.data.events || [];
}

export async function fetchLeadsQuickNotesFromApi(signal) {
  const res = await api.get("/leads/quick-notes", signal ? { signal } : undefined);
  return res.data.templates || [];
}

export async function fetchLeadsSocietiesFromApi(signal) {
  const res = await api.get("/societies", signal ? { signal } : undefined);
  return res.data.societies || [];
}

export async function fetchLeadsStatsFromApi(params, signal) {
  const res = await api.get("/leads/stats", {
    params,
    ...(signal ? { signal } : {}),
  });
  return res.data;
}

export async function fetchLeadsListFromApi(params, signal) {
  const res = await api.get("/leads", {
    params,
    ...(signal ? { signal } : {}),
  });
  return {
    leads: res.data.leads || [],
    total: res.data.total || 0,
  };
}
